import React, { useState, useEffect, useCallback } from 'react';
import PartnerLayout from '@/components/layouts/PartnerLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { usePartner, API } from '@/contexts/PartnerContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Users, Plus, Loader2, Building2, CheckCircle, Copy, Check, Zap } from 'lucide-react';

export default function PartnerCustomers() {
  const { headers } = usePartner();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', business_name: '', plan_id: '' });
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/partner/customers`, { headers });
      setCustomers(data || []);
    } catch { toast.error('Failed to load merchants'); }
    finally { setLoading(false); }
  }, []);

  // Fetch public plans once
  useEffect(() => {
    axios.get(`${API}/plans/public`)
      .then(r => setPlans((r.data || []).filter(p => p.is_active)))
      .catch(() => {});
  }, []);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const handleAdd = async () => {
    if (!form.full_name || !form.email) { toast.error('Name and email are required'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/partner/customers`, form, { headers });
      setCreated(data);
      const selectedPlan = plans.find(p => p.id === form.plan_id);
      setCustomers(prev => [{
        id: data.merchant_id, full_name: form.full_name, email: form.email,
        business_name: form.business_name,
        plan_name: selectedPlan?.name || 'Free',
        commission_earned: 0, created_at: new Date().toISOString()
      }, ...prev]);
      setForm({ full_name: '', email: '', business_name: '', plan_id: '' });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add merchant');
    } finally { setSaving(false); }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <PartnerLayout title="Customers">
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">{customers.length} referred merchant{customers.length !== 1 ? 's' : ''}</p>
          <Button onClick={() => { setCreated(null); setModalOpen(true); }} className="gap-2" data-testid="add-customer-btn">
            <Plus className="h-4 w-4" /> Add Merchant
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>
        ) : customers.length === 0 ? (
          <Card><CardContent className="py-16 text-center">
            <Users className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="text-muted-foreground">No merchants yet. Add your first merchant or share your referral link.</p>
          </CardContent></Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="customers-table">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Merchant</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Plan</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Per TX Commission</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Extra TX Commission</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Monthly Commission</th>
                      <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground">Total Commission</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {customers.map(c => (
                      <tr key={c.id} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                              <Building2 className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="font-medium text-foreground leading-tight text-[13px]">{c.full_name}</p>
                              <p className="text-[11px] text-muted-foreground">{c.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-[13px] text-muted-foreground capitalize">{c.plan_name || 'Free'}</td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-emerald-400 text-sm">
                          ${(c.per_tx_commission || 0).toFixed(4)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-amber-400 text-sm">
                          ${(c.extra_tx_commission || 0).toFixed(4)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-semibold text-blue-400 text-sm">
                          ${(c.monthly_commission || 0).toFixed(4)}
                        </td>
                        <td className="px-4 py-3 text-right font-mono font-bold text-foreground text-sm">
                          ${((c.per_tx_commission || 0) + (c.extra_tx_commission || 0) + (c.monthly_commission || 0)).toFixed(4)}
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(c.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-sm" data-testid="add-customer-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus className="h-4 w-4" /> Add Merchant</DialogTitle>
          </DialogHeader>

          {created ? (
            <div className="space-y-3 py-1">
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-center">
                <CheckCircle className="h-8 w-8 text-emerald-500 mx-auto mb-2" />
                <p className="font-semibold text-foreground">Merchant Created!</p>
                <p className="text-sm text-muted-foreground">{created.email}</p>
                <p className="text-xs text-muted-foreground mt-0.5">A welcome email has been sent with login credentials.</p>
              </div>
              {created.temp_password && (
                <div className="bg-muted/50 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Temporary Password</p>
                  <div className="flex items-center gap-2">
                    <code className="text-sm font-mono flex-1">{created.temp_password}</code>
                    <button onClick={() => copy(created.temp_password)}>
                      {copied ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              )}
              <Button className="w-full" onClick={() => { setModalOpen(false); setCreated(null); }}>Done</Button>
            </div>
          ) : (
            <div className="space-y-3 py-1">
              <div><Label className="text-xs">Full Name *</Label>
                <Input value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} placeholder="John Doe" data-testid="cust-name" /></div>
              <div><Label className="text-xs">Email *</Label>
                <Input type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} placeholder="john@example.com" data-testid="cust-email" /></div>
              <div><Label className="text-xs">Business Name</Label>
                <Input value={form.business_name} onChange={e => setForm(p => ({ ...p, business_name: e.target.value }))} placeholder="ACME Store (optional)" data-testid="cust-biz" /></div>
              {/* Plan selector */}
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  <Zap className="h-3.5 w-3.5 text-muted-foreground" /> Plan
                </Label>
                <div className="grid gap-2">
                  {plans.map(plan => (
                    <label key={plan.id}
                      className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                        form.plan_id === plan.id
                          ? 'border-primary bg-primary/8 ring-1 ring-primary/30'
                          : 'border-border hover:border-muted-foreground/30'
                      }`}
                      data-testid={`plan-option-${plan.id}`}
                    >
                      <div className="flex items-center gap-2.5">
                        <input type="radio" className="accent-primary" name="plan"
                          value={plan.id} checked={form.plan_id === plan.id}
                          onChange={() => setForm(p => ({ ...p, plan_id: plan.id }))} />
                        <div>
                          <p className="text-sm font-medium text-foreground">{plan.name}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {plan.percent_fee}% tx fee · {plan.tx_limit} tx/mo
                          </p>
                        </div>
                      </div>
                      <span className={`text-sm font-bold ${plan.monthly_price === 0 ? 'text-emerald-500' : 'text-foreground'}`}>
                        {plan.monthly_price === 0 ? 'Free' : `$${plan.monthly_price}/mo`}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
                <Button onClick={handleAdd} disabled={saving} data-testid="create-customer-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Create
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PartnerLayout>
  );
}
